import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TranslationKeys } from 'localization';

import { Button, TextArea, Flex, H3 } from 'design-system-web';

import { StarRating } from './StarRating';
import styles from './ServiceRequestFeedbackForm.scss';

const TRANSLATION_KEYS: { [key: string]: TranslationKeys } = Object.freeze({
  submit: 'web.member.serviceRequest.feedbackModal.submit',
  skip: 'web.member.serviceRequest.feedbackModal.skip',
  howDidWeDo: 'web.member.serviceRequest.feedbackModal.howDidWeDo',
  additionalComments:
    'web.member.serviceRequest.feedbackModal.additionalComments',
  shareFeedback: 'web.member.serviceRequest.feedbackModal.shareFeedback',
  requestId: 'web.member.serviceRequest.feedbackModal.requestId',
  status: 'web.member.serviceRequest.feedbackModal.status',
  categoryIssue: 'web.member.serviceRequest.feedbackModal.categoryIssue',
  categoryIssueSeparator:
    'web.member.serviceRequest.feedbackModal.categoryIssueSeparator',
  pleaseSelectRating:
    'web.member.serviceRequest.feedbackModal.pleaseSelectRating',
  error: 'web.member.serviceRequest.feedbackModal.error',
});

export type FeedbackFormData = {
  rating: number;
  comment?: string;
};

type FeedbackFormProps = {
  onSubmit: (data: FeedbackFormData) => void;
  onSkip: () => void;
  loading?: boolean;
};

export const ServiceRequestFeedbackForm = ({
  onSubmit,
  onSkip,
  loading: externalLoading = false,
}: FeedbackFormProps) => {
  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    formState: { isValid, isSubmitting },
  } = useForm<FeedbackFormData>({
    mode: 'onChange',
    defaultValues: {
      rating: undefined, // we don't want to set a default value for the rating as 0 to validate the form
      comment: undefined,
    },
  });

  const isFormSubmitting = isSubmitting || externalLoading;

  const handleFormSubmit = (data: FeedbackFormData) => {
    onSubmit(data);
  };

  const handleSkipClick = () => {
    onSkip();
  };

  return (
    <>
      <Flex direction="column" align="center" className={styles.ratingSection}>
        <H3 className={styles.ratingTitle}>{t(TRANSLATION_KEYS.howDidWeDo)}</H3>
        <Controller
          name="rating"
          control={control}
          rules={{
            required: true,
            min: { value: 1, message: t(TRANSLATION_KEYS.pleaseSelectRating) },
          }}
          render={({ field: { onChange, value } }) => (
            <StarRating rating={value || 0} onRatingChange={onChange} />
          )}
        />
      </Flex>
      <Flex direction="column" className={styles.commentsSection}>
        <label className={styles.commentsLabel}>
          {t(TRANSLATION_KEYS.additionalComments)}
        </label>
        <Controller
          name="comment"
          control={control}
          render={({ field: { onChange, value, onBlur } }) => (
            <TextArea
              value={value || ''}
              testId="feedback-textarea"
              onChange={onChange}
              onBlur={onBlur}
              placeholder={t(TRANSLATION_KEYS.shareFeedback)}
              className={styles.commentsTextarea}
              maxLength={200}
              minRows={4}
            />
          )}
        />
      </Flex>
      <Flex gap={3} className={styles.actions}>
        <Button
          variant="primary"
          onClick={handleSubmit(handleFormSubmit)}
          disabled={!isValid || isFormSubmitting}
          loading={isFormSubmitting}
          size="large"
        >
          {t(TRANSLATION_KEYS.submit)}
        </Button>
        <Button
          variant="text"
          onClick={handleSkipClick}
          disabled={isFormSubmitting}
          size="large"
        >
          {t(TRANSLATION_KEYS.skip)}
        </Button>
      </Flex>
    </>
  );
};
